import { apiFetch, API_BASE } from "./api.js";

/** Get all knowledge base entries for a persona. */
export async function getKnowledge(personaId) {
  return apiFetch(`/api/knowledge/${personaId}`);
}

/** Add a knowledge base entry (admin only). Embedding generated server-side. */
export async function addKnowledgeEntry(personaId, { topic, content, sourceUrl, sourceTitle, groupId }) {
  return apiFetch(`/api/knowledge/${personaId}`, {
    method: "POST",
    body: JSON.stringify({ topic, content, sourceUrl, sourceTitle, groupId }),
  });
}

/** Update an existing entry (admin only). Embedding regenerated server-side. */
export async function updateKnowledgeEntry(id, updates) {
  return apiFetch(`/api/knowledge/${id}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

/** Hard-delete a knowledge entry (admin only). */
export async function deleteKnowledgeEntry(id) {
  return apiFetch(`/api/knowledge/${id}`, { method: "DELETE" });
}

// ── KB Groups (KB-004) ──────────────────────────────────────────────────────

/** Get all knowledge groups for a persona. */
export async function getKnowledgeGroups(personaId) {
  return apiFetch(`/api/knowledge/${personaId}/groups`);
}

/** Create a knowledge group. */
export async function createKnowledgeGroup(personaId, { name, policyArea }) {
  return apiFetch(`/api/knowledge/${personaId}/groups`, {
    method: "POST",
    body: JSON.stringify({ name, policyArea }),
  });
}

/** Update a knowledge group. */
export async function updateKnowledgeGroup(groupId, updates) {
  return apiFetch(`/api/knowledge/groups/${groupId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

/** Delete a knowledge group. */
export async function deleteKnowledgeGroup(groupId) {
  return apiFetch(`/api/knowledge/groups/${groupId}`, { method: "DELETE" });
}

/** Assign entries to a group. */
export async function assignEntriesToGroup(groupId, entryIds) {
  return apiFetch(`/api/knowledge/groups/${groupId}/assign`, {
    method: "POST",
    body: JSON.stringify({ entryIds }),
  });
}

/** Unassign entries from a group. */
export async function unassignEntriesFromGroup(groupId, entryIds) {
  return apiFetch(`/api/knowledge/groups/${groupId}/unassign`, {
    method: "POST",
    body: JSON.stringify({ entryIds }),
  });
}

/** Regenerate summary for a group. */
export async function regenerateGroupSummary(groupId) {
  return apiFetch(`/api/knowledge/groups/${groupId}/regenerate-summary`, {
    method: "POST",
  });
}

/** Suggest a group for given entry text. */
export async function suggestGroupForEntry(personaId, text) {
  return apiFetch(`/api/knowledge/${personaId}/suggest-group`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

// ── KB-017: Context Budget ──────────────────────────────────────────────────

/** Get context budget utilization for a persona. */
export async function getContextBudget(personaId) {
  return apiFetch(`/api/knowledge/${personaId}/budget`);
}

/** KB-020: Set a custom KB group limit for a persona (admin only). Pass null to reset to default. */
export async function setKbLimit(personaId, maxKbUnits) {
  return apiFetch(`/api/admin/personas/${personaId}/kb-limit`, {
    method: "PATCH",
    body: JSON.stringify({ maxKbUnits }),
  });
}

// ── KB-020: Batch URL Ingestion (Background Jobs with SSE) ─────────────────

/** Subscribe to job updates via SSE, falling back to polling if SSE fails.
 * Returns the final job result. Calls onProgress(job) on each update. */
export function subscribeToJob(jobId, { onProgress } = {}) {
  const API_SECRET = import.meta.env.VITE_API_SECRET ?? "";
  const sseUrl = `${API_BASE}/api/knowledge/jobs/${jobId}/stream?secret=${encodeURIComponent(API_SECRET)}`;

  return new Promise((resolve, reject) => {
    // Try SSE first
    let es;
    try {
      es = new EventSource(sseUrl);
    } catch {
      // EventSource not supported — fall back to polling
      return pollJobFallback(jobId, { onProgress }).then(resolve, reject);
    }

    const cleanup = () => { try { es.close(); } catch {} };

    es.addEventListener("progress", (e) => {
      try {
        const job = JSON.parse(e.data);
        if (onProgress) onProgress(job);
      } catch { /* ignore parse errors */ }
    });

    es.addEventListener("completed", (e) => {
      cleanup();
      try {
        const job = JSON.parse(e.data);
        resolve(job.result);
      } catch { reject(new Error("Failed to parse result")); }
    });

    es.addEventListener("failed", (e) => {
      cleanup();
      try {
        const job = JSON.parse(e.data);
        reject(new Error(job.error ?? "Job failed"));
      } catch { reject(new Error("Job failed")); }
    });

    es.onerror = () => {
      cleanup();
      // SSE connection failed — fall back to polling
      pollJobFallback(jobId, { onProgress }).then(resolve, reject);
    };
  });
}

/** Polling fallback when SSE is unavailable. */
async function pollJobFallback(jobId, { interval = 2000, maxAttempts = 90, onProgress } = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    const job = await apiFetch(`/api/knowledge/jobs/${jobId}`);
    if (onProgress) onProgress(job);
    if (job.status === "completed") return job.result;
    if (job.status === "failed") throw new Error(job.error ?? "Job failed");
    await new Promise((r) => setTimeout(r, interval));
  }
  throw new Error("Job timed out — please try again");
}

/** Import from a single URL (background job with SSE). Returns preview entries. */
export async function importFromUrl(personaId, url, { onProgress } = {}) {
  const { jobId } = await apiFetch(`/api/knowledge/${personaId}/from-url`, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
  return subscribeToJob(jobId, { onProgress });
}

/** Batch fetch + extract + dedup + auto-group from multiple URLs (background job with SSE). */
export async function batchImportFromUrls(personaId, urls, { onProgress } = {}) {
  const { jobId } = await apiFetch(`/api/knowledge/${personaId}/from-urls`, {
    method: "POST",
    body: JSON.stringify({ urls }),
  });
  return subscribeToJob(jobId, { onProgress });
}

/** Bulk save approved batch entries. */
export async function bulkSaveEntries(personaId, entries) {
  return apiFetch(`/api/knowledge/${personaId}/bulk-save`, {
    method: "POST",
    body: JSON.stringify({ entries }),
  });
}
